/**
 * Offline map features cache service.
 * Caches GeoJSON features from the map API in IndexedDB for offline access.
 * Follows existing patterns from damage-assessment.service.ts.
 */

import { db, CachedMapFeature } from './db';

export class OfflineMapFeaturesService {
  /**
   * Cache features from a server response into IndexedDB.
   */
  async cacheFromServer(
    tenantId: string,
    features: Array<{ properties: { id: string; layer: string }; [key: string]: unknown }>
  ): Promise<number> {
    if (!features.length) return 0;

    const records: CachedMapFeature[] = features.map((f) => ({
      id: `${f.properties.layer}:${f.properties.id}`,
      tenant_id: tenantId,
      layer: f.properties.layer,
      feature_id: f.properties.id,
      geojson: f as Record<string, unknown>,
      is_synced: true,
      cached_at: Date.now(),
    }));

    await db.cachedMapFeatures.bulkPut(records);
    return records.length;
  }

  /**
   * Get all cached features for a tenant, optionally filtered by layers.
   */
  async getCachedFeatures(
    tenantId: string,
    layers?: string[]
  ): Promise<CachedMapFeature[]> {
    let collection = db.cachedMapFeatures.where('tenant_id').equals(tenantId);

    const results = await collection.toArray();

    if (layers && layers.length > 0) {
      return results.filter((f) => layers.includes(f.layer));
    }
    return results;
  }

  /**
   * Clear stale cached features older than maxAge (default 24 hours).
   */
  async clearStaleCache(maxAgeMs: number = 24 * 60 * 60 * 1000): Promise<number> {
    const cutoff = Date.now() - maxAgeMs;
    const stale = await db.cachedMapFeatures.where('cached_at').below(cutoff).toArray();
    const ids = stale.map((f) => f.id);
    await db.cachedMapFeatures.bulkDelete(ids);
    return ids.length;
  }

  /**
   * Clear all cached features for a tenant.
   */
  async clearCache(tenantId: string): Promise<void> {
    const all = await db.cachedMapFeatures.where('tenant_id').equals(tenantId).toArray();
    await db.cachedMapFeatures.bulkDelete(all.map((f) => f.id));
  }
}

export const offlineMapFeaturesService = new OfflineMapFeaturesService();
